import PropTypes from 'prop-types';
import Image from '@base/Image';
import styled from '@emotion/styled';
import isEmptyObject from '@utils/isEmptyObject';
import { Spacings } from '@styles/index';
import useLevelDisplayInfo from '@api/hooks/profile/useLevelDisplayInfo';
import NextImageAvatar from '@cont/MeDashboard/NextImageAvatar';
import { PARTNER_EDUCATOR_CONFIG } from './constants';

// do we need to make different mapping for avatar size config
const avatarSizeConfig = {
  S: {
    totalWidth: '48px',
    avatarWidth: '40px',
    badgeWidth: '14px',
    avatarBorder: '2px solid var(--color-base-1)',
    badgeContainerPadding: '1.5px 0',
    badgePositionLeft: '30px'
  },
  M: {
    totalWidth: '80px',
    avatarWidth: '66.67px',
    badgeWidth: '20px',
    avatarBorder: '3px solid var(--color-base-1)',
    badgeContainerPadding: '2.5px 0',
    badgePositionLeft: '50px'
  },
  L: {
    totalWidth: '128px',
    avatarWidth: '106.67px',
    badgeWidth: '36px',
    avatarBorder: '4px solid var(--color-base-1)',
    badgeContainerPadding: '3px 0',
    badgePositionLeft: '80px'
  },
  XL: {
    totalWidth: '160px',
    avatarWidth: '133.33px',
    badgeWidth: '53.33px',
    avatarBorder: '5px solid var(--color-base-1)',
    badgeContainerPadding: '4px 0',
    badgePositionLeft: '113.33px'
  }
};

const platinumAvatarSizeConfig = {
  L: {
    totalWidth: '128px',
    avatarWidth: '128px',
    badgeWidth: '0px',
    avatarBorder: '4px solid var(--color-base-1)',
    badgeContainerPadding: '3px 0',
    badgePositionLeft: '80px'
  }
};

const Flex = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled(Flex)`
  box-sizing: content-box;
  width: ${({ $size }) => avatarSizeConfig[$size].totalWidth};
  height: ${({ $size }) => avatarSizeConfig[$size].totalWidth};
  position: relative;
  padding: ${({ $size }) => avatarSizeConfig[$size].wrapperPadding};
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ $badgeColor }) =>
    `linear-gradient(rgba(255,255,255,0) 0 40%, ${$badgeColor})`};
`;
const PlatinumWrapper = styled(Flex)`
  box-sizing: content-box;
  width: ${({ $size }) => avatarSizeConfig[$size].totalWidth};
  height: ${({ $size }) => avatarSizeConfig[$size].totalWidth};
  position: relative;
  padding: ${({ $size }) => avatarSizeConfig[$size].wrapperPadding};

  flex: 0 0 auto;
  border: 1px solid var(--color-i-gray-v2);
  border-radius: 8px;
`;

const BadgeContainer = styled.div`
  position: absolute;
  display: flex;
  left: ${({ $size }) => avatarSizeConfig[$size].badgePositionLeft};
  bottom: 0%;
  clip-path: polygon(0px 26%, 51% 0px, 100% 25%, 100% 75%, 50% 100%, 0px 75%);
  background: ${({ $badgeBgColor }) => $badgeBgColor || 'var(--color-base-1)'};
  justify-content: center;
  align-items: center;
  padding: ${({ $size }) => avatarSizeConfig[$size].badgeContainerPadding};
  border-radius: ${Spacings.SPACING_2};
`;

const StyledAvatar = styled(NextImageAvatar)`
  border: ${({ $size }) => avatarSizeConfig[$size].avatarBorder};
`;

const EducatorAvatar = ({
  size,
  src,
  levelInfo,
  isPlatinumEducator,
  badgeBgColor,
  isPartnerEducator
}) => {
  const { levelsDisplayInfo = [] } = useLevelDisplayInfo();

  let currentLevelDisplayInfo = levelsDisplayInfo?.find?.(
    (levelDisplayInfo) => {
      return (
        levelDisplayInfo.levelId ===
        (levelInfo?.currentLevelId || levelInfo?.current_level_id)
      );
    }
  );

  currentLevelDisplayInfo = isPartnerEducator
    ? PARTNER_EDUCATOR_CONFIG
    : currentLevelDisplayInfo;
  if (
    !isPlatinumEducator &&
    !isPartnerEducator &&
    (isEmptyObject(levelInfo) || !currentLevelDisplayInfo)
  ) {
    return (
      <NextImageAvatar size={avatarSizeConfig[size].totalWidth} src={src} />
    );
  }
  const CurrentWrapper = isPlatinumEducator ? PlatinumWrapper : Wrapper;
  const CurrentAvatarConfig = avatarSizeConfig;
  if (isPlatinumEducator) {
    CurrentAvatarConfig.L = platinumAvatarSizeConfig.L;
  }
  return (
    <CurrentWrapper
      $size={size}
      $badgeColor={currentLevelDisplayInfo?.color.primary}
    >
      <StyledAvatar
        size={CurrentAvatarConfig[size].avatarWidth}
        src={src}
        circular={!isPlatinumEducator}
        $size={size}
      />
      {!isPlatinumEducator && (
        <BadgeContainer $size={size} $badgeBgColor={badgeBgColor}>
          <Image
            src={currentLevelDisplayInfo?.asset.badge}
            width={avatarSizeConfig[size].badgeWidth}
            height={avatarSizeConfig[size].badgeWidth}
            alt="badge"
          />
        </BadgeContainer>
      )}
    </CurrentWrapper>
  );
};

export default EducatorAvatar;

EducatorAvatar.propTypes = {
  size: PropTypes.oneOf(['S', 'M', 'L', 'XL']).isRequired,
  src: PropTypes.string,
  levelInfo: PropTypes.shape(PropTypes.any)
};

EducatorAvatar.defaultProps = {
  src: '',
  levelInfo: {}
};
